/**
 * Video Stories Section JavaScript
 * Handles modal, navigation, and touch gestures
 */

// Prevent duplicate class declarations
if (typeof VideoStories === 'undefined') {
  window.VideoStories = class VideoStories {
  constructor(sectionId) {
    // Prevent duplicate instances
    if (window.videoStoriesInstances && window.videoStoriesInstances[sectionId]) {
      console.warn(`VideoStories instance for section ${sectionId} already exists, returning existing instance`);
      return window.videoStoriesInstances[sectionId];
    }
    
    console.log(`Creating new VideoStories instance for section: ${sectionId}`);
    this.sectionId = sectionId;
    this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
    this.modal = document.getElementById(`video-stories-modal-${sectionId}`);
    this.videoPlayer = this.modal?.querySelector('.video-player');
    this.videoTitle = this.modal?.querySelector('.video-title');
    this.stories = [];
    this.currentIndex = 0;
    this.isModalOpen = false;
    this.hasShownSwipeHints = false;
    this.isMuted = false; // Track mute state across videos
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.minSwipeDistance = 50;
    this.isSwiping = false;
    this.currentPlayPromise = null; // Track current play promise
    this.controlsSetup = false; // Track if controls are already setup
    this.toggleLock = false; // Prevent overlapping toggles
    this.lastToggleAt = 0; // Timestamp guard to suppress rapid double toggles
    
    // Register this instance globally
    window.videoStoriesInstances = window.videoStoriesInstances || {};
    window.videoStoriesInstances[sectionId] = this;
    
    this.init();
  }
  
  init() {
    if (!this.section || !this.modal) return;
    
    this.collectStories();
    this.bindEvents();
    this.setupKeyboardNavigation();
    this.setupTouchNavigation();
  }
  
  collectStories() {
    const storyContainers = this.section.querySelectorAll('.story-container');
    this.stories = Array.from(storyContainers).map((container, index) => ({
      index,
      element: container,
      videoUrl: container.dataset.videoUrl,
      title: container.dataset.storyTitle,
    })).filter(story => story.videoUrl);
  }
  
  bindEvents() {
    // Story click events
    this.stories.forEach(story => {
      story.element.addEventListener('click', () => this.openModal(story.index));
      story.element.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openModal(story.index);
        }
      });
    });
    
    // Modal close events
    const closeBtn = this.modal.querySelector('.video-modal-close');
    const overlay = this.modal.querySelector('.video-modal-overlay');
    
    closeBtn?.addEventListener('click', () => this.closeModal());
    overlay?.addEventListener('click', () => this.closeModal());
    
    // Navigation buttons
    const prevBtn = this.modal.querySelector('.video-nav-prev');
    const nextBtn = this.modal.querySelector('.video-nav-next');
    
    prevBtn?.addEventListener('click', () => this.previousVideo());
    nextBtn?.addEventListener('click', () => this.nextVideo());
  }
  
  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (!this.isModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          this.closeModal();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.previousVideo();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextVideo();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.nextVideo();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.previousVideo();
          break;
      }
    });
  }
  
  setupTouchNavigation() {
    // Disable swipe navigation to prevent interference with scrolling and video clicks
    // Users can use navigation buttons or keyboard controls instead
    console.log('Touch navigation disabled to prevent scrolling interference');
  }
  
  openModal(storyIndex = 0) {
    if (this.isModalOpen) return;
    
    this.currentIndex = storyIndex;
    this.isModalOpen = true;
    
    console.log('[KEEN SLIDER] Initializing video stories slider');
    
    // Force remove any conflicting classes that block scrolling
    document.body.classList.remove('bundle-drawer-open');
    
    // Reset body styles that might block scrolling
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.height = '';
    
    this.modal.style.display = 'flex';
    
    // Initialize Keen Slider for smooth transitions
    this.initializeKeenSlider();
    
    // Setup fixed mute button
    this.setupFixedMuteButton();
    
    // Add scroll debug listeners for navigation
    this.addScrollDebugListeners();
    
    this.trackVideoOpen(this.stories[storyIndex]);
  }
  
  closeModal() {
    this.isModalOpen = false;
    
    // Remove scroll debug listeners
    this.removeScrollDebugListeners();
    
    // Destroy Keen Slider
    if (this.keenSlider) {
      this.keenSlider.destroy();
      this.keenSlider = null;
    }
    
    // Pause all videos
    const allVideos = this.modal.querySelectorAll('video');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
    
    this.modal.style.display = 'none';
    
    // Focus management - return focus to the story that was clicked
    const storyContainer = this.section.querySelector(`[data-story-index="${this.currentIndex}"]`);
    if (storyContainer) {
      storyContainer.focus();
    }
  }
  
  nextVideo() {
    if (!this.isModalOpen || !this.keenSlider) return;
    
    // Use Keen Slider for smooth transition
    this.keenSlider.next();
  }
  
  previousVideo() {
    if (!this.isModalOpen || !this.keenSlider) return;
    
    // Use Keen Slider for smooth transition
    this.keenSlider.prev();
  }

  initializeKeenSlider() {
    const sliderContainer = this.modal.querySelector('.video-stories-slider');
    if (!sliderContainer) return;

    // Clear existing content
    sliderContainer.innerHTML = '';

    // Create slides for each story
    this.stories.forEach((story, index) => {
      const slide = document.createElement('div');
      slide.className = 'keen-slider__slide';
      slide.innerHTML = `
        <div class="video-container">
          <video class="video-player" 
                 playsinline 
                 preload="auto"
                 data-story-index="${index}">
            <source src="${story.videoUrl}" type="video/mp4">
            Your browser does not support the video tag.
          </video>
          
          <!-- Custom Video Controls -->
          <div class="custom-video-controls">
            <button class="video-play-pause-btn" aria-label="Play/Pause">
              <svg class="play-icon" width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
              </svg>
              <svg class="pause-icon" width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: none;">
                <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          
          <!-- Video Timeline -->
          <div class="video-timeline">
            <div class="timeline-track">
              <div class="timeline-progress"></div>
              <div class="timeline-handle"></div>
            </div>
          </div>

          <!-- Video Title -->
          <div class="video-title">${story.title || ''}</div>
        </div>
      `;
      sliderContainer.appendChild(slide);
    });

    // Initialize Keen Slider with precise control
    this.keenSlider = new KeenSlider(sliderContainer, {
      mode: "snap",
      slides: { perView: 1, spacing: 0 },
      vertical: true,
      drag: true,
      loop: true,
      initial: this.currentIndex,
      rubberband: false, // Disable rubber band for precise control
      created: () => {
        console.log('[KEEN SLIDER] Video stories slider created');
        this.setupSlideControls();
        this.loadCurrentVideo();
      },
      slideChanged: (instance) => {
        const newIndex = instance.track.details.rel;
        console.log('[KEEN SLIDER] Slide changed to:', newIndex);
        this.currentIndex = newIndex;
        this.loadCurrentVideo();
        this.trackVideoChange(this.stories[newIndex], 'slide');
      },
      dragStarted: () => {
        // Don't pause video on drag start - let video clicks work normally
        console.log('[KEEN SLIDER] Drag started - not pausing video to allow clicks');
        this.wasPausedForDrag = this.videoPlayer ? this.videoPlayer.paused : true;
      },
      dragEnded: () => {
        // Resume video after drag if it was playing
        if (!this.wasPausedForDrag && this.videoPlayer) {
          setTimeout(() => {
            this.videoPlayer.play().catch(e => console.log('Resume after drag failed:', e));
          }, 100);
        }
      }
    });
  }

  setupSlideControls() {
    // Setup controls for all slides
    const slides = this.modal.querySelectorAll('.keen-slider__slide');
    slides.forEach((slide, index) => {
      const video = slide.querySelector('.video-player');
      const playPauseBtn = slide.querySelector('.video-play-pause-btn');

      if (video && playPauseBtn) {
        // Video click to play/pause
        video.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const playPauseBtn = slide.querySelector('.video-play-pause-btn');
          setTimeout(() => {
            if (!video.paused) {
              console.log('[VIDEO CLICK] Video is playing - pausing it');
              video.pause();
              // Show pause icon when paused (user interaction)
              this.updatePlayPauseButtonForSlide(slide, true, true);
            } else {
              console.log('[VIDEO CLICK] Video is paused - playing it');
              video.play().then(() => {
                // Show play icon when playing (user interaction) - will fade away
                this.updatePlayPauseButtonForSlide(slide, false, true);
              }).catch(e => {
                console.log('Play failed:', e);
                // Show pause icon if play fails (user interaction)
                this.updatePlayPauseButtonForSlide(slide, true, true);
              });
            }
          }, 100);
        });

        // Make play/pause button non-interactive - video handles all clicks
        playPauseBtn.style.pointerEvents = 'none';
        
        // Ensure icons are also non-interactive
        const playIcon = playPauseBtn.querySelector('.play-icon');
        const pauseIcon = playPauseBtn.querySelector('.pause-icon');
        if (playIcon) playIcon.style.pointerEvents = 'none';
        if (pauseIcon) pauseIcon.style.pointerEvents = 'none';

        // Setup timeline functionality
        this.setupTimeline(slide, video);

        // Auto-advance when video ends
        video.addEventListener('ended', () => {
          console.log('Video ended, auto-advancing to next video');
          this.nextVideo();
        });
      }

    });
  }


  setupFixedMuteButton() {
    const fixedMuteBtn = this.modal.querySelector('.video-modal-mute');
    if (!fixedMuteBtn) return;

    // Remove any existing listeners
    fixedMuteBtn.replaceWith(fixedMuteBtn.cloneNode(true));
    const newMuteBtn = this.modal.querySelector('.video-modal-mute');
    
    newMuteBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Trigger scale animation on each click
      newMuteBtn.style.transform = 'scale(1.1)';
      newMuteBtn.style.transition = 'transform 0.1s ease';
      console.log('[MUTE] Button scaled to 1.1 on click');
      
      // Toggle mute state
      this.isMuted = !this.isMuted;
      console.log('[MUTE] Button clicked - toggled to:', this.isMuted);
      
      // Apply to current video
      if (this.videoPlayer) {
        this.videoPlayer.muted = this.isMuted;
        this.videoPlayer.volume = this.isMuted ? 0 : 1.0;
        console.log('[MUTE] Applied to video - muted:', this.videoPlayer.muted);
      }
      
      // Update button visual immediately with direct DOM manipulation
      const unmuteIcon = newMuteBtn.querySelector('.unmute-icon');
      const muteIcon = newMuteBtn.querySelector('.mute-icon');
      
      console.log('[MUTE] Found icons - unmute:', !!unmuteIcon, 'mute:', !!muteIcon);
      
      if (unmuteIcon && muteIcon) {
        if (this.isMuted) {
          unmuteIcon.style.display = 'none';
          muteIcon.style.display = 'block';
          console.log('[MUTE] Direct update - showing mute icon');
        } else {
          unmuteIcon.style.display = 'block';
          muteIcon.style.display = 'none';
          console.log('[MUTE] Direct update - showing unmute icon');
        }
        
        // Reset button scale after 0.1 seconds
        setTimeout(() => {
          newMuteBtn.style.transform = 'scale(1)';
          newMuteBtn.style.transition = 'transform 0.3s ease';
          console.log('[MUTE] Button scale reset to 1 after 0.1s');
        }, 100);
        
      } else {
        console.log('[MUTE] ERROR: Icons not found for direct update');
      }
    });

    // Update initial visual state
    this.updateFixedMuteButton();
    console.log('[MUTE] Fixed mute button setup complete');
  }

  updateMuteButtonVisual(muteBtn) {
    if (!muteBtn) return;

    const unmuteIcon = muteBtn.querySelector('.unmute-icon');
    const muteIcon = muteBtn.querySelector('.mute-icon');

    if (!unmuteIcon || !muteIcon) {
      console.log('[MUTE] Icons not found in button');
      return;
    }

    if (this.isMuted) {
      // Video is muted - show mute icon (crossed out speaker)
      unmuteIcon.style.display = 'none';
      muteIcon.style.display = 'block';
      console.log('[MUTE] Showing mute icon - video is muted');
    } else {
      // Video is unmuted - show unmute icon (normal speaker)
      unmuteIcon.style.display = 'block';
      muteIcon.style.display = 'none';
      console.log('[MUTE] Showing unmute icon - video has sound');
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    console.log('[MUTE] Toggled mute state to:', this.isMuted);

    // Apply mute state to current video only
    if (this.videoPlayer) {
      this.videoPlayer.muted = this.isMuted;
      this.videoPlayer.volume = this.isMuted ? 0 : 1.0;
      console.log('[MUTE] Applied to video - muted:', this.videoPlayer.muted, 'volume:', this.videoPlayer.volume);
    }

    // Force immediate visual update with a small delay to ensure DOM is ready
    setTimeout(() => {
      this.updateFixedMuteButton();
    }, 10);

    console.log('[MUTE] Applied mute state to current video:', this.isMuted);
  }

  updateFixedMuteButton() {
    const fixedMuteBtn = this.modal.querySelector('.video-modal-mute');
    if (!fixedMuteBtn) {
      console.log('[MUTE] Fixed mute button not found');
      return;
    }

    const unmuteIcon = fixedMuteBtn.querySelector('.unmute-icon');
    const muteIcon = fixedMuteBtn.querySelector('.mute-icon');

    if (!unmuteIcon || !muteIcon) {
      console.log('[MUTE] Mute icons not found - unmute:', !!unmuteIcon, 'mute:', !!muteIcon);
      return;
    }

    // Use internal state for immediate updates, not video property which may lag
    console.log('[MUTE] Updating button - isMuted:', this.isMuted);

    if (this.isMuted) {
      // Video is muted - show mute icon (crossed out speaker)
      unmuteIcon.style.display = 'none';
      muteIcon.style.display = 'block';
      console.log('[MUTE] Fixed button showing mute icon - video is muted');
    } else {
      // Video is unmuted - show unmute icon (normal speaker)
      unmuteIcon.style.display = 'block';
      muteIcon.style.display = 'none';
      console.log('[MUTE] Fixed button showing unmute icon - video has sound');
    }
  }

  loadCurrentVideo() {
    const currentSlide = this.modal.querySelector(`.keen-slider__slide:nth-child(${this.currentIndex + 1})`);
    if (!currentSlide) return;

    const video = currentSlide.querySelector('.video-player');
    if (!video) return;

    // Pause all other videos and reset their play/pause buttons
    const allVideos = this.modal.querySelectorAll('.video-player');
    allVideos.forEach(v => {
      if (v !== video) {
        v.pause();
        v.currentTime = 0;
        // Hide play/pause button for paused videos (non-user interaction)
        const slide = v.closest('.keen-slider__slide');
        if (slide) {
          this.updatePlayPauseButtonForSlide(slide, true, false); // Hide button
        }
      }
    });

    // Set current video player reference
    this.videoPlayer = video;
    this.videoTitle = currentSlide.querySelector('.video-title');

    // Show title for first 3 seconds, then fade out
    if (this.videoTitle) {
      this.videoTitle.style.opacity = '1';
      this.videoTitle.style.transition = 'opacity 0.5s ease';
      
      // Clear any existing timeout
      if (this.titleTimeout) {
        clearTimeout(this.titleTimeout);
      }
      
      // Fade out after 3 seconds
      this.titleTimeout = setTimeout(() => {
        if (this.videoTitle) {
          this.videoTitle.style.opacity = '0';
          console.log('[TITLE] Faded out after 3 seconds');
        }
      }, 3000);
      
      console.log('[TITLE] Showing title for 3 seconds');
    }

    // Apply mute state to current video
    video.muted = this.isMuted;
    video.volume = this.isMuted ? 0 : 1.0;

    // Update fixed mute button visual
    this.updateFixedMuteButton();

    // Auto-play with small delay to avoid conflicts with manual controls
    setTimeout(() => {
      if (video.readyState >= 3) {
        // Video is already loaded, play immediately
        video.play().then(() => {
          // Hide button for auto-play (non-user interaction)
          this.updatePlayPauseButtonForSlide(currentSlide, false, false);
        }).catch(e => {
          console.log('Auto-play failed:', e);
          // Hide button if auto-play fails (non-user interaction)
          this.updatePlayPauseButtonForSlide(currentSlide, true, false);
        });
        console.log('[KEEN SLIDER] Auto-playing preloaded video:', this.currentIndex);
      } else {
        // Video needs loading
        video.addEventListener('canplay', () => {
          video.play().then(() => {
            // Hide button for auto-play (non-user interaction)
            this.updatePlayPauseButtonForSlide(currentSlide, false, false);
          }).catch(e => {
            console.log('Auto-play after load failed:', e);
            // Hide button if auto-play fails (non-user interaction)
            this.updatePlayPauseButtonForSlide(currentSlide, true, false);
          });
        }, { once: true });
        
        video.load();
        console.log('[KEEN SLIDER] Loading video for auto-play:', this.currentIndex);
      }
    }, 100);

    // Preload adjacent videos to prevent black flash
    this.preloadAdjacentVideos();
  }

  preloadAdjacentVideos() {
    const nextIndex = (this.currentIndex + 1) % this.stories.length;
    const prevIndex = this.currentIndex === 0 ? this.stories.length - 1 : this.currentIndex - 1;

    // Preload next video
    const nextSlide = this.modal.querySelector(`.keen-slider__slide:nth-child(${nextIndex + 1})`);
    if (nextSlide) {
      const nextVideo = nextSlide.querySelector('.video-player');
      if (nextVideo && nextVideo.readyState < 3) {
        nextVideo.preload = 'auto';
        nextVideo.muted = this.isMuted; // Apply mute state to preloaded video
        nextVideo.volume = this.isMuted ? 0 : 1.0;
        nextVideo.load();
        console.log('[PRELOAD] Loading next video:', nextIndex, 'muted:', this.isMuted);
      }
    }

    // Preload previous video
    const prevSlide = this.modal.querySelector(`.keen-slider__slide:nth-child(${prevIndex + 1})`);
    if (prevSlide) {
      const prevVideo = prevSlide.querySelector('.video-player');
      if (prevVideo && prevVideo.readyState < 3) {
        prevVideo.preload = 'auto';
        prevVideo.muted = this.isMuted; // Apply mute state to preloaded video
        prevVideo.volume = this.isMuted ? 0 : 1.0;
        prevVideo.load();
        console.log('[PRELOAD] Loading previous video:', prevIndex, 'muted:', this.isMuted);
      }
    }
  }

  updateMuteButtonVisual(slide) {
    const muteBtn = slide.querySelector('.video-mute-btn');
    if (!muteBtn) return;

    const unmuteIcon = muteBtn.querySelector('.unmute-icon');
    const muteIcon = muteBtn.querySelector('.mute-icon');

    if (!unmuteIcon || !muteIcon) {
      console.log('[MUTE] Missing mute icons in slide');
      return;
    }

    if (this.isMuted) {
      // Video is muted - show mute icon (crossed out speaker)
      unmuteIcon.style.display = 'none';
      muteIcon.style.display = 'block';
      console.log('[MUTE] Showing mute icon - video is muted');
    } else {
      // Video is unmuted - show unmute icon (normal speaker)
      unmuteIcon.style.display = 'block';
      muteIcon.style.display = 'none';
      console.log('[MUTE] Showing unmute icon - video has sound');
    }

    console.log('[MUTE] Updated button visual for slide, muted:', this.isMuted);
  }

  loadVideo(story) {
    if (!this.videoPlayer || !story.videoUrl) return;
    
    // Show loading state
    this.videoPlayer.setAttribute('data-loading', 'true');
    
    // Apply current mute state
    this.videoPlayer.muted = this.isMuted;
    this.videoPlayer.volume = this.isMuted ? 0 : 1.0;
    
    console.log('Loading video, applying mute state:', this.isMuted, 'volume:', this.videoPlayer.volume);
    
    // Update video source
    const source = this.videoPlayer.querySelector('source');
    if (source) {
      source.src = story.videoUrl;
    } else {
      this.videoPlayer.src = story.videoUrl;
    }
    
    // Update title
    if (this.videoTitle) {
      this.videoTitle.textContent = story.title || '';
    }
    
    // Load and play
    this.videoPlayer.load();
    
    // Add auto-advance to next video when current video ends
    this.videoPlayer.addEventListener('ended', () => {
      console.log('Video ended, auto-advancing to next video');
      this.nextVideo();
    }, { once: true });
    
    // Remove loading state when ready
    this.videoPlayer.addEventListener('loadeddata', () => {
      this.videoPlayer.removeAttribute('data-loading');
      
      // Maintain mute state after load
      this.videoPlayer.muted = this.isMuted;
      this.videoPlayer.volume = this.isMuted ? 0 : 1.0;
      
      console.log('Video loaded, final muted:', this.videoPlayer.muted, 'volume:', this.videoPlayer.volume);
      
      // Show controls initially
      const customControls = this.modal.querySelector('.custom-video-controls');
      if (customControls) {
        customControls.classList.remove('hide');
      }
      // Initialize mute icon
      this.updateMuteIcon();
    }, { once: true });
    
    // Auto-play (with error handling)
    this.currentPlayPromise = this.videoPlayer.play();
    if (this.currentPlayPromise !== undefined) {
      this.currentPlayPromise.then(() => {
        this.currentPlayPromise = null;
      }).catch(error => {
        this.currentPlayPromise = null;
        console.log('Auto-play prevented:', error);
        // Show controls when auto-play fails
        const customControls = this.modal.querySelector('.custom-video-controls');
        if (customControls) {
          customControls.classList.remove('hide');
        }
        // Show controls when auto-play fails - handled by updatePlayPauseButtonForSlide
      });
    }
  }
  
  trackVideoOpen(story) {
    // Analytics tracking for video open
    if (typeof gtag !== 'undefined') {
      gtag('event', 'video_story_open', {
        'story_title': story.title,
        'story_index': story.index,
        'section_id': this.sectionId
      });
    }
  }
  
  trackVideoChange(story, direction) {
    // Analytics tracking for video navigation
    if (typeof gtag !== 'undefined') {
      gtag('event', 'video_story_navigate', {
        'story_title': story.title,
        'story_index': story.index,
        'direction': direction,
        'section_id': this.sectionId
      });
    }
  }
  
  // Public method to programmatically open a story
  openStory(index) {
    this.openModal(index);
  }
  
  // Public method to get current story info
  getCurrentStory() {
    return this.stories[this.currentIndex];
  }
  
  
  toggleMute() {
    if (!this.videoPlayer) return;
    
    // Toggle the persistent mute state
    this.isMuted = !this.isMuted;
    
    // Apply to current video
    this.videoPlayer.muted = this.isMuted;
    this.videoPlayer.volume = this.isMuted ? 0 : 1.0;
    
    this.updateMuteIcon();
  }
  
  updatePlayPauseButtonForSlide(slide, showPause, isUserInteraction = false) {
    if (!slide) return;
    
    const playPauseBtn = slide.querySelector('.video-play-pause-btn');
    const playIcon = slide.querySelector('.play-icon');
    const pauseIcon = slide.querySelector('.pause-icon');
    
    if (playPauseBtn && playIcon && pauseIcon) {
      if (showPause) {
        // Video is paused - show pause icon (two sticks) only on user interaction
        if (isUserInteraction) {
          playPauseBtn.classList.add('show');
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
          console.log('[PLAY/PAUSE] Showing pause icon for slide - video is paused');
        } else {
          // Hide button for non-user interactions (video switches, auto-play)
          playPauseBtn.classList.remove('show');
        }
      } else {
        // Video is playing - show play icon (triangle) briefly then fade away
        if (isUserInteraction) {
          playPauseBtn.classList.add('show');
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
          console.log('[PLAY/PAUSE] Showing play icon for slide - video is playing');
          
          // Fade away play icon after 1 second
          setTimeout(() => {
            playPauseBtn.classList.remove('show');
            console.log('[PLAY/PAUSE] Fading away play icon - video is playing');
          }, 400);
        } else {
          // Hide button for non-user interactions
          playPauseBtn.classList.remove('show');
        }
      }
    }
  }

  setupTimeline(slide, video) {
    const timeline = slide.querySelector('.video-timeline');
    const track = slide.querySelector('.timeline-track');
    const progress = slide.querySelector('.timeline-progress');
    const handle = slide.querySelector('.timeline-handle');
    
    if (!timeline || !track || !progress || !handle || !video) {
      console.log(`[TIMELINE] Missing elements - timeline: ${!!timeline}, track: ${!!track}, progress: ${!!progress}, handle: ${!!handle}, video: ${!!video}`);
      return;
    }
    
    console.log(`[TIMELINE] All elements found - setting up timeline for video`);
    
    // Make timeline visible for debugging
    timeline.style.display = 'block';
    timeline.style.visibility = 'visible';
    timeline.style.opacity = '1';
    
    // Force progress bar visibility
    progress.style.display = 'block';
    progress.style.visibility = 'visible';
    progress.style.opacity = '1';
    progress.style.background = '#fff';
    progress.style.zIndex = '10';
    progress.style.position = 'absolute';
    
    console.log(`[TIMELINE] Timeline element styles:`, {
      display: timeline.style.display,
      visibility: timeline.style.visibility,
      opacity: timeline.style.opacity,
      bottom: timeline.style.bottom,
      height: timeline.style.height
    });
    
    console.log(`[TIMELINE] Progress element styles:`, {
      display: progress.style.display,
      visibility: progress.style.visibility,
      opacity: progress.style.opacity,
      background: progress.style.background,
      zIndex: progress.style.zIndex,
      width: progress.style.width
    });

    let isDragging = false;

    // Update timeline progress as video plays
    const updateProgress = () => {
      if (!isDragging && video.duration && video.duration > 0) {
        const progressPercent = (video.currentTime / video.duration) * 100;
        progress.style.width = `${Math.max(0, Math.min(100, progressPercent))}%`;
        progress.style.background = '#fff';
        progress.style.display = 'block';
        progress.style.opacity = '1';
        progress.style.zIndex = '10';
        handle.style.left = `${Math.max(0, Math.min(100, progressPercent))}%`;
        console.log(`[TIMELINE] Progress: ${progressPercent.toFixed(1)}% (${video.currentTime.toFixed(1)}s / ${video.duration.toFixed(1)}s) - Width: ${progress.style.width}, Background: ${progress.style.background}`);
      } else {
        console.log(`[TIMELINE] Cannot update - isDragging: ${isDragging}, duration: ${video.duration}`);
      }
    };

    // Update progress on time update
    video.addEventListener('timeupdate', updateProgress);
    
    // Initial progress update when video loads
    video.addEventListener('loadedmetadata', () => {
      updateProgress();
      console.log(`[TIMELINE] Video loaded - Duration: ${video.duration}s`);
    });
    
    // Force initial update after a short delay
    setTimeout(() => {
      if (video.duration) {
        updateProgress();
        console.log(`[TIMELINE] Force update - Duration: ${video.duration}s, Current: ${video.currentTime}s`);
      }
    }, 500);

    // Handle click on timeline track
    const seekToPosition = (e) => {
      e.stopPropagation(); // Prevent video click handler
      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
      
      if (video.duration) {
        const newTime = (percentage / 100) * video.duration;
        video.currentTime = newTime;
        progress.style.width = `${percentage}%`;
        handle.style.left = `${percentage}%`;
      }
    };

    // Click to seek
    track.addEventListener('click', seekToPosition);
    
    // Prevent timeline clicks from bubbling to video
    timeline.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // Handle dragging
    const startDrag = (e) => {
      isDragging = true;
      e.preventDefault();
      seekToPosition(e);
    };

    const drag = (e) => {
      if (isDragging) {
        e.preventDefault();
        seekToPosition(e);
      }
    };

    const endDrag = () => {
      isDragging = false;
    };

    // Mouse events
    handle.addEventListener('mousedown', startDrag);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', endDrag);

    // Touch events for mobile
    handle.addEventListener('touchstart', (e) => {
      startDrag(e.touches[0]);
    });

    document.addEventListener('touchmove', (e) => {
      if (isDragging) {
        drag(e.touches[0]);
      }
    });

    document.addEventListener('touchend', endDrag);

    console.log('[TIMELINE] Timeline setup complete for slide');
  }

  
  updateMuteIcon() {
    // Update icons for standalone mute button
    const unmuteIcon = this.modal.querySelector('.video-container > .video-mute-btn .unmute-icon');
    const muteIcon = this.modal.querySelector('.video-container > .video-mute-btn .mute-icon');
    
    if (unmuteIcon && muteIcon && this.videoPlayer) {
      if (this.videoPlayer.muted) {
        unmuteIcon.style.display = 'none';
        muteIcon.style.display = 'block';
      } else {
        unmuteIcon.style.display = 'block';
        muteIcon.style.display = 'none';
      }
    }
  }

  setupMuteButton() {
    const muteBtn = this.modal.querySelector('.video-mute-btn');
    console.log('Looking for mute button, found:', muteBtn);
    
    if (muteBtn) {
      // Remove any existing listeners
      muteBtn.replaceWith(muteBtn.cloneNode(true));
      const newMuteBtn = this.modal.querySelector('.video-mute-btn');
      
      newMuteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Mute button clicked');
        this.toggleMute();
        this.hideSwipeHints();
      });
      
      console.log('Mute button event listener attached');
    } else {
      console.log('Mute button not found');
    }
  }

  showSwipeHintsIfFirstTime() {
    // Check if we've already shown hints (stored in sessionStorage)
    const hasSeenHints = sessionStorage.getItem(`video-stories-hints-${this.sectionId}`);
    
    if (!hasSeenHints && !this.hasShownSwipeHints) {
      const swipeIndicators = this.modal.querySelector('.mobile-swipe-indicators');
      if (swipeIndicators) {
        swipeIndicators.classList.add('show-first-time');
        this.hasShownSwipeHints = true;
        
        // Auto-hide after 3 seconds
        setTimeout(() => {
          this.hideSwipeHints();
        }, 3000);
      }
    }
  }
  
  hideSwipeHints() {
    const swipeIndicators = this.modal.querySelector('.mobile-swipe-indicators');
    if (swipeIndicators) {
      swipeIndicators.classList.remove('show-first-time');
      // Mark as seen in sessionStorage (resets on page refresh)
      sessionStorage.setItem(`video-stories-hints-${this.sectionId}`, 'true');
    }
  }

  addScrollDebugListeners() {
    console.log('[SCROLL DEBUG] Adding precise scroll event listeners for video navigation');
    
    // Throttle scroll events to prevent multiple rapid triggers
    let scrollTimeout = null;
    let lastScrollTime = 0;
    
    // Track scroll for video navigation
    this.scrollListeners = {
      wheel: (e) => {
        e.preventDefault();
        
        const now = Date.now();
        if (now - lastScrollTime < 300) return; // Throttle to 300ms
        
        console.log('[SCROLL DEBUG] Wheel event for navigation:', e.deltaY);
        
        if (e.deltaY > 0) {
          // Scroll down = next video
          console.log('[SCROLL DEBUG] Navigating to next video');
          this.nextVideo();
          lastScrollTime = now;
        } else if (e.deltaY < 0) {
          // Scroll up = previous video
          console.log('[SCROLL DEBUG] Navigating to previous video');
          this.previousVideo();
          lastScrollTime = now;
        }
      },
      touchstart: (e) => {
        this.touchStartY = e.touches[0].clientY;
        this.touchStartTime = Date.now();
        console.log('[SCROLL DEBUG] Touch start for navigation:', this.touchStartY);
      },
      touchmove: (e) => {
        if (!this.touchStartY) return;
        
        const touchCurrentY = e.touches[0].clientY;
        const touchDelta = this.touchStartY - touchCurrentY;
        
        // Prevent default scrolling behavior for vertical swipes
        if (Math.abs(touchDelta) > 10) {
          e.preventDefault();
        }
        
        console.log('[SCROLL DEBUG] Touch move delta:', touchDelta);
      },
      touchend: (e) => {
        if (!this.touchStartY) return;
        
        const touchEndY = e.changedTouches[0].clientY;
        const touchDelta = this.touchStartY - touchEndY;
        const touchDuration = Date.now() - this.touchStartTime;
        
        console.log('[SCROLL DEBUG] Touch end delta:', touchDelta, 'duration:', touchDuration);
        
        // Only trigger navigation if swipe is significant and not too slow
        if (Math.abs(touchDelta) > 50 && touchDuration < 500) {
          // Prevent video click events when navigating
          e.preventDefault();
          e.stopPropagation();
          
          if (touchDelta > 0) {
            // Swipe up = next video
            console.log('[SCROLL DEBUG] Touch swipe to next video');
            this.nextVideo();
          } else {
            // Swipe down = previous video
            console.log('[SCROLL DEBUG] Touch swipe to previous video');
            this.previousVideo();
          }
        } else if (Math.abs(touchDelta) < 10 && touchDuration < 300) {
          // Small movement and quick touch = tap for play/pause
          console.log('[SCROLL DEBUG] Touch tap detected - allowing video click');
          // Don't prevent default - let video click handler work
        }
        
        this.touchStartY = null;
        this.touchStartTime = null;
      }
    };
    
    // Add listeners to modal overlay (not video elements) to avoid interfering with video clicks
    const modalOverlay = this.modal.querySelector('.video-modal-overlay');
    const modalContainer = this.modal.querySelector('.video-modal-container');
    
    if (modalOverlay) {
      modalOverlay.addEventListener('wheel', this.scrollListeners.wheel, { passive: false });
      modalOverlay.addEventListener('touchstart', this.scrollListeners.touchstart, { passive: true });
      modalOverlay.addEventListener('touchmove', this.scrollListeners.touchmove, { passive: false });
      modalOverlay.addEventListener('touchend', this.scrollListeners.touchend, { passive: true });
    }
    
    if (modalContainer) {
      modalContainer.addEventListener('wheel', this.scrollListeners.wheel, { passive: false });
    }
    
    console.log('[SCROLL DEBUG] Precise video navigation listeners added');
  }

  removeScrollDebugListeners() {
    if (this.scrollListeners) {
      console.log('[SCROLL DEBUG] Removing video navigation listeners');
      
      const modalOverlay = this.modal.querySelector('.video-modal-overlay');
      const modalContainer = this.modal.querySelector('.video-modal-container');
      
      if (modalOverlay) {
        modalOverlay.removeEventListener('wheel', this.scrollListeners.wheel);
        modalOverlay.removeEventListener('touchstart', this.scrollListeners.touchstart);
        modalOverlay.removeEventListener('touchmove', this.scrollListeners.touchmove);
        modalOverlay.removeEventListener('touchend', this.scrollListeners.touchend);
      }
      
      if (modalContainer) {
        modalContainer.removeEventListener('wheel', this.scrollListeners.wheel);
      }
      
      this.scrollListeners = null;
      this.touchStartY = null;
      this.touchStartTime = null;
    }
  }

  // Cleanup method
  destroy() {
    this.removeScrollDebugListeners();
    this.closeModal();
    // Remove event listeners if needed for cleanup
  }
}

// Global registry to prevent duplicate instances
window.videoStoriesInstances = window.videoStoriesInstances || {};

// Initialize Video Stories sections when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOMContentLoaded - Initializing VideoStories sections');
  // Initialize any existing video stories sections
  const videoStoriesSections = document.querySelectorAll('.video-stories-section');
  console.log('Found video stories sections:', videoStoriesSections.length);
  
  videoStoriesSections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    console.log('Processing section:', sectionId, 'existing instance:', !!window.videoStoriesInstances[sectionId]);
    
    if (sectionId && !window.videoStoriesInstances[sectionId]) {
      // Store instance globally to prevent duplicates
      window.videoStoriesInstances[sectionId] = new VideoStories(sectionId);
      section.videoStoriesInstance = window.videoStoriesInstances[sectionId];
      console.log('Created new VideoStories instance for section:', sectionId);
    } else if (sectionId && window.videoStoriesInstances[sectionId]) {
      // Reuse existing instance
      section.videoStoriesInstance = window.videoStoriesInstances[sectionId];
      console.log('Reused existing VideoStories instance for section:', sectionId);
    }
  });
});

// Handle section reloading in theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.video-stories-section');
    if (section) {
      const sectionId = section.dataset.sectionId;
      console.log('Shopify section:load for:', sectionId, 'existing instance:', !!window.videoStoriesInstances[sectionId]);
      
      if (sectionId && !window.videoStoriesInstances[sectionId]) {
        window.videoStoriesInstances[sectionId] = new VideoStories(sectionId);
        section.videoStoriesInstance = window.videoStoriesInstances[sectionId];
        console.log('Created VideoStories instance for reloaded section:', sectionId);
      } else if (sectionId && window.videoStoriesInstances[sectionId]) {
        section.videoStoriesInstance = window.videoStoriesInstances[sectionId];
        console.log('Reused existing VideoStories instance for reloaded section:', sectionId);
      }
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.video-stories-section');
    if (section && section.videoStoriesInstance) {
      const sectionId = section.dataset.sectionId;
      console.log('Shopify section:unload for:', sectionId);
      
      section.videoStoriesInstance.destroy();
      if (sectionId && window.videoStoriesInstances[sectionId]) {
        delete window.videoStoriesInstances[sectionId];
        console.log('Cleaned up VideoStories instance for section:', sectionId);
      }
    }
  });
}

// Export for external use
window.VideoStories = VideoStories;
} else {
  // Use existing VideoStories class
  window.VideoStories = VideoStories;
}
